'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["callProvider", "fromNumber", "callMethod"];
import { Map as ImmutableMap } from 'immutable';
import { AsyncSettingsOmnibusResult } from '../records/SettingsOmnibusResult';
import { getOrCreateUniversalCallingStore } from 'calling-global-api/universal-calling-store/UniversalCallingStore';
import { getIsProviderHubSpot, getIsTwilioBasedCallProvider } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import CallingProvider from 'calling-internal-common/call-provider/records/CallingProvider';
import FromNumber from 'calling-internal-common/calls-v1-settings/records/InitialLoadSettingsRecords/FromNumber';
import { getHubSpotCallingProvider } from 'calling-lifecycle-internal/call-provider/operators/callProviderOperators';
import { CONNECT_FROM_NUMBER, FROM_NUMBER, getPersistedCallMethod, getPersistedCallProvider, getPersistedFromNumber, getPersistedFromNumberWithFallback, setPersistedCallProvider } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { SET_CALL_METHOD, SET_CALLING_PROVIDER_SETTING } from 'calling-global-api/constants/EventListeners';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getConnectAccountNumbers, getHubspotProvidedNumbers, getHubspotSubAccountNumbers, getPhoneNumbers, getCallProvidersFromOmnibusResult, getHubSpotCallingEnabled } from '../operators/settingsOmnibusResultGetters';
import { FAILED, SUCCEEDED } from 'calling-lifecycle-internal/async-flux-utils/constants/apiLoadingStates';
let settingsOmnibusStore;
export const getSubjectKey = ({
  subjectId,
  objectTypeId
}) => {
  if (!subjectId || !objectTypeId) {
    return null;
  }
  return `${subjectId}_${objectTypeId}`;
};
export const setSettingsOminbusStoreForTesting = store => {
  settingsOmnibusStore = store;
};
export const parseRawState = state => {
  const {
      callProvider,
      fromNumber,
      callMethod
    } = state,
    omnibusInformation = _objectWithoutPropertiesLoose(state, _excluded);
  const parsedOmnibusInformation = {};
  if (omnibusInformation) {
    Object.keys(omnibusInformation).forEach(key => {
      if (!omnibusInformation[key]) return;
      parsedOmnibusInformation[key] = new AsyncSettingsOmnibusResult(omnibusInformation[key]);
    });
  }
  return ImmutableMap(Object.assign({
    callProvider: new CallingProvider(callProvider),
    fromNumber: new FromNumber(fromNumber),
    callMethod
  }, parsedOmnibusInformation));
};
export function handleSetCallProvider(callProvider) {
  let fromNumber = null;
  let callMethod = null;
  if (getIsTwilioBasedCallProvider(callProvider)) {
    const fromNumberKey = getIsProviderHubSpot(callProvider) ? FROM_NUMBER : CONNECT_FROM_NUMBER;
    fromNumber = getPersistedFromNumber(fromNumberKey);
    if (getIsProviderHubSpot(callProvider)) {
      callMethod = getPersistedCallMethod();
    }
  }
  const state = settingsOmnibusStore.getState();
  settingsOmnibusStore.setState(state.merge({
    callProvider,
    fromNumber,
    callMethod
  }));
}
export function initializeCallProvider(omnibusData, initializingOmnibus = false) {
  const persistedCallProvider = getPersistedCallProvider();

  // If HS calling is enabled and not first withCalleeOmnibus initialization or HubSpot not selected
  if (getHubSpotCallingEnabled(omnibusData) && !initializingOmnibus || !getIsProviderHubSpot(persistedCallProvider)) {
    return;
  }
  const phoneNumbers = getPhoneNumbers(omnibusData);
  const hubspotFromNumbers = getHubspotSubAccountNumbers(phoneNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(phoneNumbers);
  // If HubSpot callProvider has registered from numbers, return
  if (hubspotFromNumbers.length || hubspotProvidedNumbers.length) {
    return;
  }
  const callProviders = getCallProvidersFromOmnibusResult(omnibusData);
  // If no other call providers exist, return
  if (callProviders.size <= 1) {
    return;
  }
  const callProvider = callProviders.get(1);
  setPersistedCallProvider(callProvider);
  handleSetCallProvider(callProvider);
}
export function handleSetProviderSettings(providerSettings) {
  handleSetCallProvider(providerSettings.callingProvider);
  handleSetFromNumber(providerSettings.fromNumber);
}
export function initializeFromNumber(phoneNumbers) {
  const state = settingsOmnibusStore.getState();
  const hubspotFromNumbers = getHubspotSubAccountNumbers(phoneNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(phoneNumbers);
  const portalAndPersonalNumbers = hubspotFromNumbers.concat(hubspotProvidedNumbers);
  const defaultFromNumber = getPersistedFromNumberWithFallback({
    fromNumberKey: FROM_NUMBER,
    fromNumbers: portalAndPersonalNumbers
  });
  const connectFromNumbers = getConnectAccountNumbers(phoneNumbers);
  const defaultConnectFromNumber = getPersistedFromNumberWithFallback({
    fromNumberKey: CONNECT_FROM_NUMBER,
    fromNumbers: connectFromNumbers
  });
  const isProviderHubSpot = getIsProviderHubSpot(state.get('callProvider'));
  const fromNumber = isProviderHubSpot ? defaultFromNumber : defaultConnectFromNumber;
  settingsOmnibusStore.setState(state.merge({
    fromNumber
  }));
}

/**
 * This function updates the persisted call provider with the newest call provider config
 * @param {*} omnibusData
 * @returns
 */
function updatePersistedCallProvider(omnibusData) {
  const persistedCallProvider = getPersistedCallProvider();

  // If the persisted call provider is HubSpot, return
  if (getIsProviderHubSpot(persistedCallProvider)) {
    return;
  }
  const callProviders = getCallProvidersFromOmnibusResult(omnibusData);
  const selectedCallProvider = callProviders.find(provider => {
    const providerAppId = provider.get('appId');
    const persistedProviderAppId = persistedCallProvider.get('appId');
    return providerAppId && persistedProviderAppId && providerAppId === persistedProviderAppId;
  });

  // If the persisted call provider is not in the omnibus data, return
  if (!selectedCallProvider) {
    return;
  }
  setPersistedCallProvider(selectedCallProvider);
  handleSetCallProvider(selectedCallProvider);
}
export function setSettingsOmnibusData({
  fetchCalleeOmnibusPromise
}) {
  fetchCalleeOmnibusPromise.then(data => {
    // Eventually this entire api call will be deleted, this is to test that the data still gets there.
    const settingsOmnibusResult = new AsyncSettingsOmnibusResult({
      state: SUCCEEDED,
      data
    });
    const omnibusData = getData(settingsOmnibusResult);

    // Everything in here is handled by the webworker

    settingsOmnibusStore.updateState(state => {
      return state.setIn(['asyncSettingsOmnibus', 'data', 'phoneNumbers'], settingsOmnibusResult.getIn(['data', 'phoneNumbers'])).setIn(['asyncSettingsOmnibus', 'data', 'installedCallingSettings'], settingsOmnibusResult.getIn(['data', 'installedCallingSettings'])).setIn(['asyncSettingsOmnibus', 'state'], settingsOmnibusResult.getIn(['state'])).setIn(['asyncSettingsOmnibus', 'data', 'callingAdminSettings'], settingsOmnibusResult.getIn(['data', 'callingAdminSettings']));
    });
    updatePersistedCallProvider(omnibusData);
    if (getIsTwilioBasedCallProvider(settingsOmnibusStore.getState().get('callProvider'))) {
      const phoneNumbers = getPhoneNumbers(omnibusData);
      initializeFromNumber(phoneNumbers);
    }
  }).catch(error => {
    settingsOmnibusStore.updateState(state => state.set('asyncSettingsOmnibus', new AsyncSettingsOmnibusResult({
      state: FAILED,
      error
    })));
  });
}
export function handleSetCallMethod(callMethod) {
  settingsOmnibusStore.updateState(state => state.set('callMethod', callMethod));
}
export function handleSetFromNumber(fromNumber) {
  settingsOmnibusStore.updateState(state => state.set('fromNumber', fromNumber));
}
export const getOrCreateSettingsOmnibusStore = (props = {}) => {
  const {
    isHelpDesk = false
  } = props;
  const callProvider = isHelpDesk ? getHubSpotCallingProvider() : getPersistedCallProvider();
  const callMethod = getIsProviderHubSpot(callProvider) ? getPersistedCallMethod() : null;
  let fromNumber = null;
  if (!isHelpDesk) {
    if (getIsTwilioBasedCallProvider(callProvider)) {
      const fromNumberKey = getIsProviderHubSpot(callProvider) ? FROM_NUMBER : CONNECT_FROM_NUMBER;
      fromNumber = getPersistedFromNumber(fromNumberKey);
    }
  }
  const initialState = ImmutableMap({
    callProvider,
    fromNumber,
    callMethod,
    asyncSettingsOmnibus: new AsyncSettingsOmnibusResult()
  });
  settingsOmnibusStore = getOrCreateUniversalCallingStore({
    name: 'settingsOmnibus',
    initialState,
    parseRawState,
    version: 1,
    // this needs to update with AsyncSettingsOmnibusResult
    eventListeners: [{
      event: SET_CALL_METHOD,
      callback: handleSetCallMethod
    }, {
      event: SET_CALLING_PROVIDER_SETTING,
      callback: handleSetProviderSettings
    }]
  });
  return settingsOmnibusStore;
};